/**
 * WebRTC-based transcription service for OpenAI Realtime API
 * Based on the hello-transcription reference implementation
 */
import type { WrappedServiceModule } from "~/hooks/useServices";
import type * as lambdaService from "~/services/lambda";
import {
  type AudioFrameAnalysis,
  analyzeAudioFrame,
  DEFAULT_SPEECH_THRESHOLDS,
  hasVoiceActivity,
  isSpeechFrame,
  type SpeechThresholds,
} from "~/utils/audioAnalysis";

/**
 * Which product surface is dictating. The proxy builds a different OpenAI
 * session per surface, and the client behaves differently to match: legacy
 * Assessment stays on the turn-based model where server VAD closes each turn,
 * while the streaming surfaces stream partials and have to close their own
 * utterances.
 *
 * `assessment-streaming` is the report-writing path on the streaming engine,
 * behind the `assessment-streaming-dictation` pilot flag. It is a distinct
 * value rather than a reuse of `"indy"` so telemetry and session config stay
 * truthful, and so legacy `"assessment"` stays pinned.
 */
export type DictationSurface =
  | "assessment"
  | "indy"
  | "assessment-streaming"
  | "exam-streaming";

/**
 * True for the surfaces running the streaming engine: partials mid-speech,
 * no server VAD, client-side utterance commits.
 *
 * This is the *transport* predicate — every audio-path and turn-handling
 * difference keys off it. Behaviour that is genuinely Indy-only (or genuinely
 * assessment-only) still tests the surface value directly.
 */
export const isStreamingSurface = (surface: DictationSurface): boolean =>
  surface === "indy" ||
  surface === "assessment-streaming" ||
  surface === "exam-streaming";

/** Model-only events; subscribers cannot replace the manager's UI callbacks. */
export type RawDictationEvent = { surface: DictationSurface } & (
  | { type: "delta" | "final"; text: string }
  | { type: "start" | "stop" | "commit" }
);

// Constants for WebRTC configuration
const WEBRTC_CONNECTION_TIMEOUT_MS = 10000; // 10 seconds
// Audio processing buffer, in samples. Assessment keeps the size it has
// always had. For streaming, every microphone frame passes through the
// ScriptProcessor on its way to WebRTC, so the buffer is pure added latency:
// 2048 samples at 24kHz held ~85ms of speech before it left the browser. 1024
// halves that while staying large enough that main-thread work does not glitch
// the audio — a dropout costs transcription accuracy, which is worse than the
// 20ms that 512 would save.
const AUDIO_PROCESSOR_BUFFER_SIZE = 2048;
const STREAMING_AUDIO_PROCESSOR_BUFFER_SIZE = 1024;

interface TranscriptionConfig {
  // No configuration needed - Lambda is self-contained
  [key: string]: unknown;
}

interface TranscriptEvent {
  text: string;
  isFinal: boolean;
  timestamp: Date;
}

type TranscriptionEventHandler = (event: TranscriptEvent) => void;
type ErrorHandler = (error: Error) => void;
type StatusHandler = () => void;

// Audio analysis data emitted for visualization
export interface AudioAnalysisData extends AudioFrameAnalysis {
  containsSpeech: boolean;
  timestamp: number;
}

type AudioAnalysisHandler = (analysis: AudioAnalysisData) => void;

// WebRTC message types
interface OpenAITranscriptionDelta {
  type: "conversation.item.input_audio_transcription.delta";
  delta: string;
}

interface OpenAITranscriptionCompleted {
  type: "conversation.item.input_audio_transcription.completed";
  transcript: string;
  item_id?: string;
}

// OpenAI could not transcribe a committed utterance. It still consumes a
// commit slot: the client must treat it as that utterance's (empty) final, or
// the commit→final pairing skews and every later final lands one sentence off.
interface OpenAITranscriptionFailed {
  type: "conversation.item.input_audio_transcription.failed";
  item_id?: string;
}

interface OpenAIError {
  type: "error";
  error: { message?: string };
}

// conversation.item.added event - contains the transcription in item.content
interface OpenAIConversationItemAdded {
  type: "conversation.item.added";
  item: {
    id: string;
    type: string;
    role?: string;
    content?: Array<{
      type: string;
      transcript?: string;
      text?: string;
    }>;
  };
}

// conversation.item.done event - signals item completion
interface OpenAIConversationItemDone {
  type: "conversation.item.done";
  item: {
    id: string;
    type: string;
    content?: Array<{
      type: string;
      transcript?: string;
      text?: string;
    }>;
  };
}

// Union type for all possible OpenAI messages
// type OpenAIMessage = OpenAITranscriptionDelta | OpenAITranscriptionCompleted | OpenAIError;

// Type guards
function isOpenAITranscriptionDelta(
  data: unknown,
): data is OpenAITranscriptionDelta {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "conversation.item.input_audio_transcription.delta" &&
    "delta" in data &&
    typeof (data as { delta: unknown }).delta === "string"
  );
}

function isOpenAITranscriptionCompleted(
  data: unknown,
): data is OpenAITranscriptionCompleted {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "conversation.item.input_audio_transcription.completed" &&
    "transcript" in data &&
    typeof (data as { transcript: unknown }).transcript === "string"
  );
}

function isOpenAITranscriptionFailed(
  data: unknown,
): data is OpenAITranscriptionFailed {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "conversation.item.input_audio_transcription.failed"
  );
}

function isOpenAIError(data: unknown): data is OpenAIError {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "error" &&
    "error" in data &&
    typeof (data as { error: unknown }).error === "object"
  );
}

function isOpenAIConversationItemAdded(
  data: unknown,
): data is OpenAIConversationItemAdded {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "conversation.item.added" &&
    "item" in data
  );
}

function isOpenAIConversationItemDone(
  data: unknown,
): data is OpenAIConversationItemDone {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "conversation.item.done" &&
    "item" in data
  );
}

/**
 * Extract transcript from conversation item content array
 */
function extractTranscriptFromItem(item: {
  content?: Array<{ type: string; transcript?: string; text?: string }>;
}): string | null {
  if (!item.content || !Array.isArray(item.content)) {
    return null;
  }

  for (const content of item.content) {
    // Look for input_audio type with transcript
    if (content.type === "input_audio" && content.transcript) {
      return content.transcript;
    }
    // Also check for text type
    if (content.type === "text" && content.text) {
      return content.text;
    }
  }

  return null;
}

// Idle timeout before closing connection (5 minutes)
const CONNECTION_IDLE_TIMEOUT_MS = 5 * 60 * 1000;

// gpt-live-transcribe runs without server VAD (it rejects any turn_detection
// block), so nothing on OpenAI's side decides where one utterance ends. The
// client closes the turn instead: this much detected silence while recording
// commits the audio buffer, which is what produces the final, punctuated
// transcript that replaces the streamed deltas.
//
// Each committed utterance is transcribed independently, so the model treats
// every one as a fresh sentence and capitalises its first word. Cutting too
// eagerly therefore sprinkles capitals through the middle of continuous speech
// — at 500ms an ordinary thinking pause was enough to start a "new sentence".
// This is deliberately long enough to ride out those pauses. Streamed deltas
// are unaffected either way, so raising it delays only when a sentence settles,
// not when words appear.
const UTTERANCE_SILENCE_MS = 900;

/**
 * How long a recording may run without voice activity before it is ended.
 *
 * A click-started dictation has nothing to close it: push-to-talk stops on
 * release, but the toggle path stays on until someone presses it again, and the
 * connection's idle timeout does not run while recording. A forgotten session
 * therefore holds the microphone indefinitely.
 *
 * The two surfaces get different limits because their cost differs by an order
 * of magnitude. The streaming surface passes the microphone through untouched
 * (see `passAudio` below), so a forgotten Indy session ships real room audio to
 * OpenAI and is billed for every second of it — worth cutting off quickly.
 * Assessment gates non-speech to zeros, so an idle session costs almost
 * nothing, and its dictation has genuinely long gaps: a physician reads and
 * examines between sentences. Cutting that off would interrupt real work to
 * save nothing.
 *
 * Being wrong is cheap in one direction only. An early stop still flushes the
 * open utterance and keeps the text — the user clicks the microphone again.
 * A late stop bills for the room. That asymmetry is why the streaming limit is
 * as short as it is.
 */
const RECORDING_SILENCE_LIMIT_MS: Record<DictationSurface, number> = {
  indy: 30 * 1000,
  assessment: 2 * 60 * 1000,
  // The pilot surface pulls both ways: it passes audio through like Indy (so
  // it bills for the room), but it serves Assessment's workflow, where a
  // physician reads and examines between sentences and the only activity
  // signal is a returned transcript. Indy's 30s would cut them off
  // mid-examination; at one pilot user the cost of being generous is about
  // three cents a forgotten session. Revisit before widening the rollout.
  "assessment-streaming": 2 * 60 * 1000,
  "exam-streaming": 2 * 60 * 1000,
};

/** How often the auto-stop is re-evaluated off the audio graph. */
const AUTO_STOP_POLL_MS = 5000;

// Temporary diagnostics for the streaming rollout: one console line per
// dictation lifecycle event, tagged with the build, so "which code is this
// browser actually running and where does the pipeline stall" is answerable
// from a screenshot instead of a guess. Strip before the flag rolls out wide.
// Bump on every behavioral change to this pipeline — the tag's whole job is
// answering "which code is this browser actually running".
const DICTATION_BUILD = "diag-r6";
const dlog = (message: string): void => {
  console.info(`[dictation ${DICTATION_BUILD}] ${message}`);
};

export class WebRTCTranscriptionService {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private audioStream: MediaStream | null = null;
  private sessionConfig: TranscriptionConfig = {};
  private isConnected = false;
  private isRecording = false;
  private lambdaService: WrappedServiceModule<typeof lambdaService> | null =
    null;
  private connectionPromise: Promise<void> | null = null;
  private connectionResolve: (() => void) | null = null;
  private connectionReject: ((error: Error) => void) | null = null;

  // Audio processing for non-speech detection
  private audioContext: AudioContext | null = null;
  private audioSource: MediaStreamAudioSourceNode | null = null;
  private audioProcessor: ScriptProcessorNode | null = null;
  private speechThresholds: SpeechThresholds = DEFAULT_SPEECH_THRESHOLDS;
  private processedAudioStream: MediaStream | null = null;

  // Audio gating - when true, silence is sent instead of audio
  private isAudioGated = true;
  private idleTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Manual utterance turns (no server VAD — see UTTERANCE_SILENCE_MS). Only the
  // streaming surface runs them; Assessment's server VAD closes its own turns.
  private silenceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // Voice-activity watchdog for the auto-stop (see RECORDING_SILENCE_LIMIT_MS).
  private lastVoiceActivityAt = 0;
  private autoStopSignalled = false;
  private autoStopPollId: ReturnType<typeof setInterval> | null = null;
  private hasUncommittedAudio = false;
  // Audio actually forwarded to OpenAI since the last commit. Distinct from
  // hasUncommittedAudio, which requires the local speech-energy threshold to
  // fire — the same threshold the streaming path bypasses for being unreliable
  // on quiet speech. Sent-but-undetected audio must still be committed at stop,
  // or a quiet dictation streams deltas and never gets a final.
  private audioSentSinceCommit = false;
  // Commit→final bookkeeping. The hook pairs finals to sealed utterance spans
  // strictly by arrival order, so this layer must guarantee "exactly one final
  // per commit of the CURRENT session": finals already delivered via
  // `transcription.completed` are deduped when the `item.done` fallback echoes
  // them (finalizedItemIds), and finals still in flight when a new session
  // starts on the reused connection are swallowed (staleFinalDebt) — the data
  // channel is ordered, so stale finals always arrive before new ones.
  private pendingFinalCount = 0;
  private staleFinalDebt = 0;
  private finalizedItemIds = new Set<string>();
  private surface: DictationSurface = "assessment";

  /** True when this session streams partials and must close its own turns. */
  private get isStreamingSurface(): boolean {
    return isStreamingSurface(this.surface);
  }

  /** Which surface the current session serves. */
  getSurface(): DictationSurface {
    return this.surface;
  }

  // Preconnect state. The sender is held so the microphone can be swapped in
  // and out without renegotiating; whether a real mic is currently attached is
  // read from `audioStream`, not tracked separately.
  private audioSender: RTCRtpSender | null = null;
  private silentAudioContext: AudioContext | null = null;
  private silentStream: MediaStream | null = null;
  // In-flight microphone release from stopRecording(). startRecording() awaits
  // it so a fast stop-then-start cannot interleave with the teardown.
  private micReleasePromise: Promise<void> | null = null;
  // Bumped by cleanup(). A release that spans a teardown must not write to the
  // session that replaced it.
  private sessionEpoch = 0;

  private rawTranscriptListeners = new Set<
    (event: RawDictationEvent) => void
  >();

  subscribeRawTranscript(
    listener: (event: RawDictationEvent) => void,
  ): () => void {
    this.rawTranscriptListeners.add(listener);
    return () => {
      this.rawTranscriptListeners.delete(listener);
    };
  }

  private emitRawTranscript(event: RawDictationEvent): void {
    for (const listener of this.rawTranscriptListeners) listener(event);
  }

  // Event handlers
  public onTranscriptReceived: TranscriptionEventHandler = () => {
    /* no-op */
  };
  public onTranscriptDelta: (delta: string) => void = () => {
    /* no-op */
  }; // Individual delta fragment of the open utterance
  public onTranscriptFinal: (transcript: string) => void = () => {
    /* no-op */
  }; // Authoritative transcript once the utterance is committed
  public onUtteranceCommitted: () => void = () => {
    /* no-op */
  }; // A commit was sent: the open utterance is sealed, its final is coming
  public onError: ErrorHandler = () => {
    /* no-op */
  };
  public onConnected: StatusHandler = () => {
    /* no-op */
  };
  public onDisconnected: StatusHandler = () => {
    /* no-op */
  };
  public onRecordingStarted: StatusHandler = () => {
    /* no-op */
  };
  public onRecordingStopped: StatusHandler = () => {
    /* no-op */
  };
  /**
   * Prolonged silence during a recording — the session looks forgotten.
   *
   * Raised rather than acted on: stopping here would skip the caller's own
   * teardown, and stopRecording()'s flush would then find nothing to commit,
   * losing the last utterance. The binder is expected to run its normal stop
   * path, which calls stopRecording() as usual.
   */
  public onAutoStopped: StatusHandler = () => {
    /* no-op */
  };
  public onAudioAnalysis: AudioAnalysisHandler = () => {
    /* no-op */
  };

  /**
   * Set the lambda service for making proxy calls
   */
  setLambdaService(service: WrappedServiceModule<typeof lambdaService>): void {
    this.lambdaService = service;
  }

  /**
   * Configure speech detection thresholds
   */
  setSpeechThresholds(thresholds: Partial<SpeechThresholds>): void {
    this.speechThresholds = { ...DEFAULT_SPEECH_THRESHOLDS, ...thresholds };
  }

  /**
   * Preload the WebRTC connection for instant first dictation.
   * Uses a synthetic silent audio stream for SDP negotiation so no
   * microphone permission is needed. The real mic is acquired later
   * in startRecording() via replaceTrack().
   * Starts idle timeout so the connection auto-closes if unused.
   * Idempotent: returns early if already connected.
   */
  async preconnect(
    service: WrappedServiceModule<typeof lambdaService>,
    surface: DictationSurface = "assessment",
  ): Promise<void> {
    if (this.isConnected && this.pc?.connectionState === "connected") {
      return;
    }

    // Guard against concurrent preconnect calls (e.g. React strict mode double-effect)
    if (this.connectionPromise !== null) {
      await this.connectionPromise;
      return;
    }

    // Adopt the surface only when this call actually negotiates a session.
    // Assessment pages preconnect on mount, so setting it above the guards let
    // a default-surface preconnect relabel a live Indy session — after which
    // commitUtterance() no-ops and, with no server VAD, no final transcript
    // ever arrives. startRecording() owns surface *changes* (it reconnects).
    this.surface = surface;

    this.lambdaService = service;

    try {
      // Use a silent audio stream for SDP negotiation (no mic permission needed)
      const silentStream = this.createSilentStream();
      this.processedAudioStream = silentStream;

      this.setupWebRTC();
      await this.connectToOpenAI();
      this.startIdleTimeout();
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  /**
   * Ensure connection is established, reconnecting if necessary.
   * Called automatically by startRecording().
   */
  private async ensureConnected(): Promise<void> {
    // Already connected and healthy
    if (this.isConnected && this.pc?.connectionState === "connected") {
      return;
    }

    // If a connection attempt is in progress (e.g., from preconnect), wait for it
    if (this.connectionPromise !== null) {
      try {
        await this.connectionPromise;
        if (this.isConnected && this.pc?.connectionState === "connected") {
          return;
        }
      } catch {
        // Connection attempt failed, fall through to reconnect
      }
    }

    // Connection dropped, timed out, or never established - (re)connect
    this.cleanup();
    await this.startSession({});
  }

  /**
   * Reset the idle timeout. Called when recording starts/stops.
   */
  private resetIdleTimeout(): void {
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }

  /**
   * Start the idle timeout countdown. Connection closes after timeout.
   */
  private startIdleTimeout(): void {
    this.resetIdleTimeout();
    this.idleTimeoutId = setTimeout(() => {
      if (!this.isRecording) {
        this.cleanup();
        this.onDisconnected();
      }
    }, CONNECTION_IDLE_TIMEOUT_MS);
  }

  /**
   * Start a transcription session with OpenAI Realtime API via WebRTC
   */
  private async startSession(config: TranscriptionConfig): Promise<void> {
    if (this.isConnected) {
      throw new Error("Session already active");
    }

    this.sessionConfig = config;

    try {
      // Request microphone access
      await this.setupAudioRecording();

      // Create WebRTC peer connection
      this.setupWebRTC();

      // Create offer and exchange with OpenAI
      await this.connectToOpenAI();
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  /**
   * Stop the current transcription session
   */
  stopSession(): void {
    this.cleanup();
  }

  /**
   * Start audio recording. Establishes connection if not already connected.
   * If preconnected with a silent stream, acquires real mic and swaps tracks.
   */
  async startRecording(
    surface: DictationSurface = "assessment",
  ): Promise<void> {
    if (this.isRecording) {
      return; // Already recording
    }

    // A release from the previous stop may still be swapping tracks; letting it
    // finish first keeps it from tearing down the microphone we acquire below.
    // Its own failure path already tore the session down, so swallow here and
    // let ensureConnected() below rebuild.
    await this.micReleasePromise?.catch(() => undefined);
    this.micReleasePromise = null;

    // Finals still in flight from the previous session on this connection
    // (its stop-flush above all) must not be delivered into the new session:
    // the hook would pair them with the NEW session's sealed spans and every
    // final after that would land one sentence early. Ordered delivery means
    // they all arrive before any new final, so swallowing exactly this many
    // is safe. cleanup() zeroes both counters when the connection is torn
    // down, since its in-flight finals die with it.
    this.staleFinalDebt += this.pendingFinalCount;
    this.pendingFinalCount = 0;

    // A session negotiated for one surface cannot serve the other — the model
    // and turn handling differ — so a surface change reconnects.
    if (this.surface !== surface) {
      this.cleanup();
      this.surface = surface;
    }

    // Ensure connection is established (lazy connect / reconnect)
    await this.ensureConnected();

    // Acquire the microphone whenever this session does not already hold one:
    // after a preconnect, after stopRecording() released it, or when this call
    // raced a preconnect still in flight. Keying off the stream rather than a
    // flag matters for that last case — connectToOpenAI() resolves before
    // preconnect() finishes its bookkeeping, so a flag read here can still be
    // false while the sender carries nothing but the silent track. Recording
    // would then start against a muted session: no error, no transcript.
    if (!this.audioStream) {
      await this.setupAudioRecording();
      if (this.audioSender && this.processedAudioStream) {
        const realTrack = this.processedAudioStream.getTracks()[0];
        if (realTrack) {
          await this.audioSender.replaceTrack(realTrack);
        }
      }
      this.cleanupSilentStream();
    }

    this.resetIdleTimeout();
    this.isAudioGated = false;
    this.isRecording = true;
    this.lastVoiceActivityAt = Date.now();
    this.autoStopSignalled = false;
    this.startAutoStopPoll();
    dlog(`recording started (surface=${this.surface})`);
    this.emitRawTranscript({ type: "start", surface: this.surface });
    this.onRecordingStarted();
  }

  /**
   * Stop audio recording. Connection stays open for reuse.
   */
  stopRecording(): void {
    if (this.isRecording) {
      // Flush before gating, so a sentence that was still mid-pause when the
      // physician stopped still comes back as a final transcript. Forced: any
      // audio that reached OpenAI deserves its final, whether or not the local
      // speech threshold ever fired.
      this.commitUtterance(true);
      this.isAudioGated = true;
      this.isRecording = false;
      this.stopAutoStopPoll();
      this.startIdleTimeout();
      this.emitRawTranscript({ type: "stop", surface: this.surface });
      this.onRecordingStopped();
      // Hand the capture device back. Gating the audio silences the transcript
      // but leaves the browser's "this tab is using your microphone" indicator
      // lit until the idle timeout finally runs cleanup() — up to five minutes
      // of a red dot with nothing listening behind it. Fire-and-forget: this
      // method must stay synchronous, since the manager relies on
      // onUtteranceCommitted having fired by the time it returns.
      this.micReleasePromise = this.releaseMicrophone();
    }
  }

  /**
   * Release the microphone while keeping the peer connection alive.
   *
   * Restores exactly the state preconnect() leaves behind: a silent track on
   * the sender and no captured stream, so the next startRecording() re-acquires
   * the mic through the existing swap path. That state is already proven to
   * hold an OpenAI session open — it is what every preconnected session sits
   * in for up to five minutes before its first utterance.
   *
   * track.enabled = false is not enough: it mutes the track but leaves the
   * capture device open, so the browser indicator stays lit. Only stop() frees
   * the device.
   */
  private async releaseMicrophone(): Promise<void> {
    if (!this.audioStream && !this.audioContext) {
      return; // Nothing held (already released, or never preconnected)
    }

    const micStream = this.audioStream;
    const processedStream = this.processedAudioStream;
    const epoch = this.sessionEpoch;
    this.audioStream = null;
    this.processedAudioStream = null;

    /** Free this release's own device handles, touching no shared state. */
    const stopCapturedStreams = () => {
      processedStream?.getTracks().forEach((track) => track.stop());
      // The one that clears the browser indicator.
      micStream?.getTracks().forEach((track) => track.stop());
    };

    try {
      // Swap first, so the sender never holds an ended track. No sender means
      // the peer connection is already unusable — fall to the catch, which
      // tears the whole thing down rather than leaving a half-live session.
      if (!this.audioSender) {
        throw new Error("no audio sender to swap the silent track onto");
      }
      // Defensive: createSilentStream() overwrites the silent-context fields,
      // which would strand a previous one.
      this.cleanupSilentStream();
      const silentTrack = this.createSilentStream().getTracks()[0];
      if (!silentTrack) {
        throw new Error("silent stream produced no track");
      }
      await this.audioSender.replaceTrack(silentTrack);

      // A teardown landed while the swap was in flight — cleanup() has already
      // reset everything, and anything written past this point would land on
      // whatever session came next.
      if (epoch !== this.sessionEpoch) {
        stopCapturedStreams();
        return;
      }

      // Mirror preconnect: both fields point at the silent stream.
      this.processedAudioStream = this.silentStream;

      if (this.audioProcessor) {
        this.audioProcessor.disconnect();
        this.audioProcessor = null;
      }
      if (this.audioSource) {
        this.audioSource.disconnect();
        this.audioSource = null;
      }
      if (this.audioContext) {
        void this.audioContext.close();
        this.audioContext = null;
      }

      stopCapturedStreams();
      dlog("microphone released, connection kept warm");
    } catch (error) {
      // Never leave the device open on a failed swap — a lit indicator with no
      // session behind it is worse than paying for a full reconnect.
      dlog(`microphone release failed: ${String(error)}`);
      stopCapturedStreams();
      // Same guard: tearing down here after a newer session began would kill it.
      if (epoch !== this.sessionEpoch) return;
      this.cleanup();
      this.onDisconnected();
    }
  }

  /** Stop any pending end-of-utterance countdown. */
  private cancelSilenceTimer(): void {
    if (this.silenceTimeoutId !== null) {
      clearTimeout(this.silenceTimeoutId);
      this.silenceTimeoutId = null;
    }
  }

  /**
   * Close the current utterance so OpenAI returns its final transcript.
   *
   * Without server VAD this is the only thing that produces a
   * `...transcription.completed` event, so it is what makes a finished sentence
   * reformat. Deltas keep streaming for the next utterance on the same session.
   */
  private commitUtterance(force = false): void {
    this.cancelSilenceTimer();

    if (!this.isStreamingSurface) {
      return;
    }
    // Silence-timer commits require detected speech; a forced flush (stop)
    // commits anything that was actually sent, detected or not.
    if (!this.hasUncommittedAudio && !(force && this.audioSentSinceCommit)) {
      return;
    }

    // A channel that cannot send keeps the flags, so the next silence window or
    // stopRecording() retries instead of silently dropping the utterance.
    // cleanup() resets them, so a torn-down session cannot leak a commit into
    // the next one.
    if (this.dc?.readyState !== "open") {
      return;
    }
    this.hasUncommittedAudio = false;
    this.audioSentSinceCommit = false;
    dlog(
      force
        ? "utterance committed (stop flush)"
        : "utterance committed (silence)",
    );
    this.dc.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    this.pendingFinalCount++;
    // After the send: a commit that could not be sent produces no final, so
    // sealing the span for it would orphan that span forever.
    this.emitRawTranscript({ type: "commit", surface: this.surface });
    this.onUtteranceCommitted();
  }

  /**
   * Close the open utterance now, from outside the audio loop.
   *
   * The manager calls this when a streaming dictation moves to a different
   * text field: the utterance dictated into the old field has to be committed
   * there so its span seals and its final still arrives, before the next
   * delta opens a span in the new field. Forced, for the same reason the stop
   * flush is — the audio reached OpenAI whether or not the local speech
   * threshold fired, so it deserves its final.
   *
   * Fires `onUtteranceCommitted` synchronously when it actually commits, and
   * does nothing at all on the legacy surface or with nothing to commit.
   */
  commitOpenUtterance(): void {
    this.commitUtterance(true);
  }

  /**
   * Get current connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isRecording: this.isRecording,
    };
  }

  /**
   * Create a silent MediaStream for SDP negotiation during preconnect.
   * No microphone permission is needed.
   */
  private createSilentStream(): MediaStream {
    this.silentAudioContext = new AudioContext({ sampleRate: 24000 });
    // Don't resume — AudioContext is suspended without a user gesture, and
    // resume() would hang. We only need destination tracks for SDP negotiation;
    // the context doesn't need to be running.
    const dest = this.silentAudioContext.createMediaStreamDestination();
    this.silentStream = dest.stream;
    return dest.stream;
  }

  /**
   * Clean up silent stream resources created during preconnect.
   */
  private cleanupSilentStream(): void {
    if (this.silentStream) {
      this.silentStream.getTracks().forEach((track) => track.stop());
      this.silentStream = null;
    }
    if (this.silentAudioContext) {
      void this.silentAudioContext.close();
      this.silentAudioContext = null;
    }
  }

  private async setupAudioRecording(): Promise<void> {
    try {
      // Request microphone access with specific constraints
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 24000 }, // OpenAI prefers 24kHz
          channelCount: 1, // Mono audio
        },
      });

      // Set up audio processing with non-speech detection
      await this.setupAudioProcessing();
    } catch (error) {
      throw new Error(
        `Microphone access denied: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private async setupAudioProcessing(): Promise<void> {
    if (!this.audioStream) {
      throw new Error("Audio stream not available for processing");
    }

    // Create AudioContext for audio processing
    this.audioContext = new AudioContext({ sampleRate: 24000 });

    // Resume AudioContext - required in modern browsers due to autoplay policies
    // Without this, onaudioprocess events won't fire until user interaction
    await this.audioContext.resume();

    // Create audio source from microphone stream
    this.audioSource = this.audioContext.createMediaStreamSource(
      this.audioStream,
    );

    // Create script processor for real-time audio analysis
    this.audioProcessor = this.audioContext.createScriptProcessor(
      this.isStreamingSurface
        ? STREAMING_AUDIO_PROCESSOR_BUFFER_SIZE
        : AUDIO_PROCESSOR_BUFFER_SIZE,
      1, // input channels
      1, // output channels
    );

    // Create destination for processed audio
    const destination = this.audioContext.createMediaStreamDestination();

    // Process audio frames and filter out non-speech
    this.audioProcessor.onaudioprocess = (event) => {
      const inputBuffer = event.inputBuffer.getChannelData(0);
      const outputBuffer = event.outputBuffer.getChannelData(0);

      // Analyze audio frame for speech content
      const analysis = analyzeAudioFrame(inputBuffer);
      const containsSpeech = isSpeechFrame(analysis, this.speechThresholds);

      // Emit audio analysis for visualization (level meters, waveforms, etc.)
      this.onAudioAnalysis({
        ...analysis,
        containsSpeech,
        timestamp: Date.now(),
      });

      // Streaming passes the microphone through untouched while recording:
      // the speech-energy threshold clipped quiet word onsets, and
      // gpt-live-transcribe handles background noise better than the
      // threshold did. Assessment keeps the original gate — its server-VAD
      // session has only ever seen thresholded audio, and feeding it raw room
      // noise changes when turns close. Either way audio only leaves the
      // browser during an active dictation.
      const passAudio = this.isStreamingSurface
        ? !this.isAudioGated
        : !this.isAudioGated && containsSpeech;
      if (passAudio) {
        outputBuffer.set(inputBuffer);
        if (this.isStreamingSurface) {
          this.audioSentSinceCommit = true;
        }
      } else {
        outputBuffer.fill(0);
      }

      // Watchdog for a forgotten session. What counts as "someone is dictating"
      // has to differ by surface, because the two transmit differently.
      //
      // Assessment sends only frames that pass containsSpeech, so that gate is
      // exactly the right signal: if nothing is going out, nothing is being
      // dictated. It is evaluated here at the 2048-sample frames its energy
      // threshold was tuned for, so it carries none of the frame-size caveat
      // that makes it wrong for the streaming path.
      //
      // The streaming surface sends every frame, so no audio-energy test can
      // separate a dictating physician from an occupied room: rms > 0.01 is
      // about -40 dBFS, which ordinary clinic ambience, HVAC and nearby
      // conversation clear continuously. Keying off it would arm the watchdog
      // in the quiet room that costs little and disarm it in the noisy one
      // that costs most — backwards. Its activity signal is a returned
      // transcript instead (see markTranscriptionActivity), which room noise
      // does not generally produce.
      if (!this.isAudioGated && !this.autoStopSignalled) {
        if (!this.isStreamingSurface && containsSpeech) {
          this.lastVoiceActivityAt = Date.now();
        }
        this.checkAutoStop();
      }

      // Drive utterance turns from voice activity: speech keeps the turn
      // open, a long enough gap closes it. Assessment's server VAD does this
      // itself, so committing from here too would double-close its turns.
      //
      // NOT the compound containsSpeech gate: its energy term is a raw sum,
      // so at this path's smaller frames it demands roughly twice the
      // amplitude, and a quiet dictation then never registers any utterance —
      // no silence commit ever fires, and the sentence-by-sentence reformat
      // degrades into one reformat at stop. RMS is frame-size independent.
      if (!this.isAudioGated && this.isStreamingSurface) {
        if (hasVoiceActivity(analysis, this.speechThresholds)) {
          if (!this.hasUncommittedAudio) {
            dlog(
              `voice activity opened a turn (rms=${analysis.rms.toFixed(4)})`,
            );
          }
          this.hasUncommittedAudio = true;
          this.cancelSilenceTimer();
        } else if (this.hasUncommittedAudio && this.silenceTimeoutId === null) {
          this.silenceTimeoutId = setTimeout(() => {
            this.silenceTimeoutId = null;
            this.commitUtterance();
          }, UTTERANCE_SILENCE_MS);
        }
      }
    };

    // Connect the audio processing chain
    this.audioSource.connect(this.audioProcessor);
    this.audioProcessor.connect(destination);

    // Store the processed stream for WebRTC
    this.processedAudioStream = destination.stream;
  }

  private setupWebRTC(): void {
    // Create RTCPeerConnection - exactly like val.town
    this.pc = new RTCPeerConnection({});

    // Set up connection state handling - exactly like val.town
    this.pc.onconnectionstatechange = () => {
      if (this.pc?.connectionState === "connected") {
        this.isConnected = true;
        this.onConnected();
        // Resolve the connection promise if it's waiting
        if (this.connectionResolve) {
          this.connectionResolve();
          this.connectionResolve = null;
          this.connectionReject = null;
        }
      } else if (this.pc?.connectionState === "failed") {
        this.isConnected = false;
        this.onDisconnected();
        // Reject the connection promise if it's waiting
        if (this.connectionReject) {
          this.connectionReject(new Error("WebRTC connection failed"));
          this.connectionResolve = null;
          this.connectionReject = null;
        }
      } else if (this.pc?.connectionState === "disconnected") {
        this.isConnected = false;
        this.onDisconnected();
      }
    };

    // Set up remote audio track handling - for receiving audio from OpenAI
    this.pc.ontrack = (ev) => {
      if (ev.track.kind === "audio") {
        // We could play this back, but for transcription-only we don't need to
      }
    };

    // Add local audio track - use processed stream with non-speech filtering.
    // Only the first track is added; mono audio streams should have exactly one.
    const streamToUse = this.processedAudioStream || this.audioStream;
    if (streamToUse) {
      const track = streamToUse.getTracks()[0];
      if (track) {
        this.audioSender = this.pc!.addTrack(track, streamToUse);
      }
    }

    // Set up data channel for receiving transcriptions - exactly like val.town
    this.dc = this.pc.createDataChannel("events", {
      ordered: true,
    });

    this.dc.onopen = () => {
      /* no-op */
    };

    this.dc.onclose = () => {
      /* no-op */
    };

    this.dc.onerror = (_error) => {
      this.onError(new Error("Data channel error"));
    };

    this.dc.onmessage = (event) => {
      this.handleDataChannelMessage(event);
    };
  }

  private async connectToOpenAI(): Promise<void> {
    if (!this.pc) {
      throw new Error("WebRTC not initialized");
    }

    try {
      // Create a promise that resolves when connection is established
      this.connectionPromise = new Promise((resolve, reject) => {
        this.connectionResolve = resolve;
        this.connectionReject = reject;

        // Add a timeout to prevent waiting forever
        setTimeout(() => {
          if (this.connectionReject) {
            this.connectionReject(new Error("WebRTC connection timeout"));
            this.connectionResolve = null;
            this.connectionReject = null;
          }
        }, WEBRTC_CONNECTION_TIMEOUT_MS);
      });

      // Prevent unhandled rejection if cleanup rejects this promise before
      // it is awaited (e.g., sendOfferToOpenAI throws before we reach the await).
      this.connectionPromise.catch(() => {
        /* error intentionally ignored */
      });

      // Create offer and send immediately - exactly like val.town
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      // Send the SDP offer to our Lambda proxy (which handles session creation)
      const answer = await this.sendOfferToOpenAI(offer.sdp!);

      // Set remote description with OpenAI's answer - exactly like val.town
      await this.pc.setRemoteDescription({
        type: "answer",
        sdp: answer,
      });

      // Wait for the actual connection to be established
      await this.connectionPromise;
    } catch (error) {
      throw new Error(
        `Failed to connect to OpenAI: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private async sendOfferToOpenAI(sdp: string): Promise<string> {
    // Use our Lambda proxy instead of direct OpenAI API call
    const lambdaService = this.lambdaService;
    if (!lambdaService) {
      throw new Error("Lambda service not available");
    }

    try {
      const result = await lambdaService.proxyWebRTCToOpenAI({
        sdp: sdp,
        surface: this.surface,
      });

      if (result.error) {
        throw new Error(`Lambda proxy error: ${result.error.message}`);
      }

      if (!result.data) {
        throw new Error("No SDP answer received from Lambda proxy");
      }

      return result.data.sdp;
    } catch (error) {
      throw new Error(
        `Failed to proxy WebRTC offer: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Deliver one utterance's final, enforcing the exactly-one-final-per-commit
   * contract the hook's span pairing depends on. `itemId` dedupes the
   * `item.done` echo of an already-completed transcription; stale finals from
   * a previous session on this connection are swallowed against the debt.
   */
  private deliverFinal(transcript: string, itemId?: string): void {
    if (itemId) {
      if (this.finalizedItemIds.has(itemId)) {
        dlog(`final deduped (item ${itemId})`);
        return;
      }
      this.finalizedItemIds.add(itemId);
    }
    if (this.staleFinalDebt > 0) {
      this.staleFinalDebt--;
      dlog("stale final from previous session swallowed");
      return;
    }
    if (this.pendingFinalCount > 0) {
      this.pendingFinalCount--;
    }
    dlog(`final received (${transcript.length} chars)`);
    this.markTranscriptionActivity(transcript);
    this.emitRawTranscript({
      type: "final",
      text: transcript,
      surface: this.surface,
    });
    this.onTranscriptFinal(transcript);
    this.onTranscriptReceived({
      text: transcript,
      isFinal: true,
      timestamp: new Date(),
    });
  }

  /**
   * Drive checkAutoStop independently of the audio graph.
   *
   * The audio callback is the prompt trigger, but it belongs to the tab's audio
   * graph, and the case this feature exists for — started a dictation, switched
   * away, forgot — is precisely where a hidden tab's callbacks may be throttled
   * or suspended. A hidden tab throttles interval timers too, typically to about
   * once a minute, but "about a minute late" is a working safety net and
   * "never" is not.
   */
  private startAutoStopPoll(): void {
    this.stopAutoStopPoll();
    this.autoStopPollId = setInterval(
      () => this.checkAutoStop(),
      AUTO_STOP_POLL_MS,
    );
  }

  private stopAutoStopPoll(): void {
    if (this.autoStopPollId !== null) {
      clearInterval(this.autoStopPollId);
      this.autoStopPollId = null;
    }
  }

  /** End the recording if nothing has counted as dictation for long enough. */
  private checkAutoStop(): void {
    if (!this.isRecording || this.isAudioGated || this.autoStopSignalled) {
      return;
    }
    if (
      Date.now() - this.lastVoiceActivityAt <=
      RECORDING_SILENCE_LIMIT_MS[this.surface]
    ) {
      return;
    }
    this.autoStopSignalled = true;
    dlog("auto-stop: no dictation activity within the silence limit");

    // Raise first, so the consumer runs its own teardown — the manager's stop
    // path flushes the open utterance and settles the UI, which stopping from
    // here would skip.
    this.onAutoStopped();

    // Then make good on it ourselves if nothing happened. Once signalled, both
    // watch paths are disarmed (the poll below, and the audio-callback check is
    // gated on autoStopSignalled), and no idle timeout is armed during a
    // recording — startRecording() clears it. So a consumer that does not act
    // would leave the microphone open forever, which is the outcome this whole
    // feature exists to prevent. useDictationManager resets onAutoStopped to a
    // no-op once every component has unmounted, so that consumer is real.
    if (this.isRecording) {
      dlog("auto-stop: consumer did not stop the recording, stopping it here");
      this.stopRecording();
    }
    this.stopAutoStopPoll();
  }

  /**
   * A transcript came back, so someone is dictating — re-arm the watchdog.
   *
   * This is the streaming surface's only activity signal (its audio is passed
   * through untouched, so energy cannot distinguish speech from room noise),
   * and it is corroborating evidence on assessment.
   *
   * Only a transcript with words in it counts. An empty one is what silence
   * produces, and the failed-transcription path routes here as "" — treating
   * either as activity re-arms the watchdog from exactly the audio it exists
   * to ignore. That closed a loop: room noise above the rms floor opens a turn,
   * the silence timer commits it, OpenAI returns nothing it could transcribe,
   * and the clock resets — so the session never ended in any occupied room.
   */
  private markTranscriptionActivity(transcript: string): void {
    if (transcript.trim().length === 0) return;
    this.lastVoiceActivityAt = Date.now();
  }

  private handleDataChannelMessage(event: MessageEvent): void {
    try {
      const rawData: unknown = JSON.parse(event.data as string);

      // Handle transcription events from OpenAI Realtime API
      if (isOpenAITranscriptionDelta(rawData)) {
        // Delta fragment - emit for real-time word-by-word display
        this.markTranscriptionActivity(rawData.delta);
        if (this.isRecording)
          this.emitRawTranscript({
            type: "delta",
            text: rawData.delta,
            surface: this.surface,
          });
        this.onTranscriptDelta(rawData.delta);
        this.onTranscriptReceived({
          text: rawData.delta,
          isFinal: false,
          timestamp: new Date(),
        });
      } else if (isOpenAITranscriptionCompleted(rawData)) {
        // Final transcript - utterance complete
        this.deliverFinal(rawData.transcript, rawData.item_id);
      } else if (isOpenAITranscriptionFailed(rawData)) {
        // A commit OpenAI could not transcribe still consumes its slot: an
        // empty final lets the hook release that utterance's sealed span, or
        // every later final would land one sentence early.
        dlog("transcription failed for a committed utterance");
        this.deliverFinal("", rawData.item_id);
      } else if (isOpenAIConversationItemDone(rawData)) {
        // Fallback: extract transcript from item.done if present. Runs
        // through the same funnel, so an item whose transcription.completed
        // already delivered is deduped instead of consuming a second span.
        const transcript = extractTranscriptFromItem(rawData.item);
        if (transcript) {
          this.deliverFinal(transcript, (rawData.item as { id?: string }).id);
        }
      } else if (isOpenAIConversationItemAdded(rawData)) {
        // Fallback: extract transcript from item.added if present
        const transcript = extractTranscriptFromItem(rawData.item);
        if (transcript) {
          this.markTranscriptionActivity(transcript);
          if (this.isRecording)
            this.emitRawTranscript({
              type: "delta",
              text: transcript,
              surface: this.surface,
            });
          this.onTranscriptDelta(transcript);
          this.onTranscriptReceived({
            text: transcript,
            isFinal: false,
            timestamp: new Date(),
          });
        }
      } else if (isOpenAIError(rawData)) {
        console.error("[WebRTC] Error from OpenAI:", rawData.error);
        this.onError(
          new Error(rawData.error.message || "Unknown OpenAI error"),
        );
      }
      // Silently ignore other message types (session.created, session.updated, etc.)
    } catch (error) {
      console.error("[WebRTC] Failed to parse message:", error);
      this.onError(new Error("Invalid message format received"));
    }
  }

  private cleanup(): void {
    // Stop recording and reset audio gate
    this.isRecording = false;
    this.isAudioGated = true;
    this.resetIdleTimeout();
    this.cancelSilenceTimer();
    this.hasUncommittedAudio = false;
    this.audioSentSinceCommit = false;
    this.pendingFinalCount = 0;
    this.staleFinalDebt = 0;
    this.finalizedItemIds.clear();

    // Reject any pending connection promise and clear all promise state
    if (this.connectionReject) {
      this.connectionReject(new Error("Session stopped during connection"));
    }
    this.connectionPromise = null;
    this.connectionResolve = null;
    this.connectionReject = null;

    // Close data channel
    if (this.dc) {
      this.dc.close();
      this.dc = null;
    }

    // Close peer connection
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    // Stop audio processing
    if (this.audioProcessor) {
      this.audioProcessor.disconnect();
      this.audioProcessor = null;
    }

    if (this.audioSource) {
      this.audioSource.disconnect();
      this.audioSource = null;
    }

    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }

    // Stop audio streams
    if (this.processedAudioStream) {
      this.processedAudioStream.getTracks().forEach((track) => track.stop());
      this.processedAudioStream = null;
    }

    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
      this.audioStream = null;
    }

    // Clean up silent stream resources (from preconnect)
    this.cleanupSilentStream();
    this.audioSender = null;
    this.micReleasePromise = null;
    this.stopAutoStopPoll();
    this.sessionEpoch++;

    this.isConnected = false;
  }
}

// Singleton instance
export const webrtcTranscriptionService = new WebRTCTranscriptionService();
